import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { InventoryMeasurementRow } from "./InventoryMeasurementFormDesktop";
import { GridColDef } from "@mui/x-data-grid-premium";
import { NumberCell } from "components/common/dataGrid/NumberCell";
import {
  Box,
  IconButton,
  Modal,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import dayjs from "dayjs";

export interface InventoryEstimateModalProps {
  open: boolean;
  rows: InventoryMeasurementRow[];
  latestDeliveryDataTimestamp: string | null;
  lastInventoryReportTimestamp: string;
  onClose: () => void;
}

const RecordedDeliveriesHeader = ({
  latestDeliveryDataTimestamp,
}: {
  latestDeliveryDataTimestamp: string | null;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const unitsFormatter = useUnitsFormatter(true);
  const { toLocalDatetime, localTimezone } = useLocalDatetime();

  const getTimestampIcon = () => {
    if (!latestDeliveryDataTimestamp) {
      return null;
    }

    const today = dayjs().tz(localTimezone).startOf("day");
    const timestampDate = dayjs(latestDeliveryDataTimestamp)
      .tz(localTimezone)
      .startOf("day");

    const formattedTimestamp = toLocalDatetime(latestDeliveryDataTimestamp);
    const tooltipText = `${t("updatedAt")}: ${formattedTimestamp}`;

    if (timestampDate.isSame(today)) {
      return (
        <Tooltip title={tooltipText}>
          <CheckCircleIcon
            sx={{
              color: theme.palette.success.main,
              fontSize: 16,
            }}
          />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title={tooltipText}>
          <WarningIcon
            sx={{
              color: theme.palette.warning.main,
              fontSize: 16,
            }}
          />
        </Tooltip>
      );
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        variant="body2"
        color={theme.palette.text.secondary}
        sx={{ flexGrow: 1 }}
      >
        {`${t("recordedDeliveries")}${unitsFormatter("mass")}`}
      </Typography>
      {getTimestampIcon()}
    </Box>
  );
};

export const LastInventoryReportHeader = ({
  lastInventoryReportTimestamp,
}: {
  lastInventoryReportTimestamp: string;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const unitsFormatter = useUnitsFormatter(true);
  const { toLocalDatetime } = useLocalDatetime();
  const formattedTimestamp = toLocalDatetime(lastInventoryReportTimestamp);
  const tooltipText = `${t("lastInventoryReport")}: ${formattedTimestamp}`;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        variant="body2"
        color={theme.palette.text.secondary}
        sx={{ flexGrow: 1 }}
      >
        {`${t("lastInventoryReport")}${unitsFormatter("mass")}`}
      </Typography>
      <Tooltip title={tooltipText}>
        <InfoOutlinedIcon
          fontSize="small"
          sx={{ color: theme.palette.text.secondary, fontSize: 16 }}
        />
      </Tooltip>
    </Box>
  );
};

export const InventoryEstimateModal = ({
  open,
  rows,
  latestDeliveryDataTimestamp,
  lastInventoryReportTimestamp,
  onClose,
}: InventoryEstimateModalProps) => {
  const { t } = useTenantTranslation();
  const unitsFormatter = useUnitsFormatter(true);
  const modalColumns: GridColDef<InventoryMeasurementRow>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      sortable: false,
      flex: 1.7,
      display: "flex",
    },
    {
      field: "previous_quantity",
      renderHeader: () => (
        <LastInventoryReportHeader
          lastInventoryReportTimestamp={lastInventoryReportTimestamp}
        />
      ),
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <NumberCell
          decimalPlaces={2}
          value={params.row.previous_quantity ?? null}
        />
      ),
      display: "flex",
    },
    {
      field: "deliveries_since_previous",
      sortable: false,
      flex: 1,
      renderHeader: () => (
        <RecordedDeliveriesHeader
          latestDeliveryDataTimestamp={latestDeliveryDataTimestamp}
        />
      ),
      renderCell: (params) => (
        <NumberCell
          decimalPlaces={2}
          value={params.row.deliveries_since_previous ?? null}
        />
      ),
      display: "flex",
    },
    {
      field: "consumption_since_previous",
      headerName: `${t("recordedConsumption")}${unitsFormatter("mass")}`,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <NumberCell
          decimalPlaces={2}
          value={params.row.consumption_since_previous ?? null}
        />
      ),
      display: "flex",
    },
    {
      field: "quantity",
      headerName: `${t("estimatedInventory")}${unitsFormatter("mass")}`,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <NumberCell
          decimalPlaces={2}
          value={params.row.quantity ?? null}
          typographyProps={{ fontWeight: "fontWeightBold" }}
        />
      ),
      display: "flex",
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        sx={{
          width: "60vw",
          height: "60vh",
          p: 3,
          outline: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4">{t("estimatedInventory")}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <HotkeyCompliantDataGrid
          rows={rows}
          initialState={{
            sorting: {
              sortModel: [{ field: "material_name", sort: "asc" }],
            },
          }}
          columns={modalColumns}
          getRowId={(row) => row.material_name}
          sx={{
            maxHeight: "85%",
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
          }}
          cellSelection
          ignoreValueFormatterDuringExport
        />
      </Paper>
    </Modal>
  );
};
